import { useEffect, useState } from 'react'
import { formatRelativeTime } from '@/lib/utils'

export interface RelativeTimeProps {
  /** Date to format */
  date: Date | string | undefined
  /** Update interval in milliseconds (default: 60000 = 1 minute) */
  updateInterval?: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Formats timestamps as "5m ago", "2h ago" with auto-update.
 * 
 * @example
 * ```tsx
 * <RelativeTime date={run.timestamps?.created} />
 * <RelativeTime date="2025-12-19T14:30:00Z" updateInterval={10000} />
 * ```
 */
export function RelativeTime({
  date,
  updateInterval = 60000,
  className,
}: RelativeTimeProps) {
  const [, setTick] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1)
    }, updateInterval)

    return () => clearInterval(interval)
  }, [updateInterval])

  if (!date) {
    return <span className={className}>—</span>
  }

  return (
    <time
      dateTime={new Date(date).toISOString()}
      title={new Date(date).toLocaleString()}
      className={className}
    >
      {formatRelativeTime(date)}
    </time>
  )
}
